import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Package,
  Image,
  FileText,
  ChevronRight,
  ExternalLink,
  Loader2,
  Save,
  Check,
  CheckCircle,
  History,
  Clock,
  Images,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import ImageGalleryPopup from "./ImageGalleryPopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderSidebar({ conversation, onOrderUpdated }) {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [imageCount, setImageCount] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Dynamic dropdown options
  const [dropdownOptions, setDropdownOptions] = useState({
    order_id_status: [],
    design_status: [],
    account: [],
    product_details: [],
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch dropdown options on mount
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const response = await axios.get(`${API}/dropdown-options`);
        const options = {};
        response.data.forEach((config) => {
          options[config.field_name] = config.options || [];
        });
        setDropdownOptions(options);
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    // Reset image count immediately when conversation changes
    setImageCount(0);
    if (conversation?.id) {
      fetchOrder();
      fetchPreviousOrders();
    } else {
      setOrder(null);
      setPreviousOrders([]);
      setEditedFields({});
      setHasChanges(false);
    }
  }, [conversation?.id]);

  const fetchPreviousOrders = async () => {
    if (!conversation?.phone_number) return;
    
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API}/orders/by-phone/${encodeURIComponent(conversation.phone_number)}`
      );
      // Filter out current order and show rest as history
      setPreviousOrders(response.data || []);
    } catch (error) {
      console.error("Error fetching previous orders:", error);
      setPreviousOrders([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchOrder = async () => {
    if (!conversation?.id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/orders/by-conversation/${conversation.id}`
      );
      setOrder(response.data);
      setEditedFields({});
      setHasChanges(false);
      // Fetch image count for this order
      fetchImageCount(response.data.id);
    } catch (error) {
      // Try fetching by phone number if not found by conversation
      if (conversation?.phone_number) {
        try {
          const phoneResponse = await axios.get(
            `${API}/orders/by-phone/${encodeURIComponent(conversation.phone_number)}`
          );
          if (phoneResponse.data.length > 0) {
            setOrder(phoneResponse.data[0]);
            setEditedFields({});
            setHasChanges(false);
            // Fetch image count for this order
            fetchImageCount(phoneResponse.data[0].id);
          } else {
            setOrder(null);
            setImageCount(0);
          }
        } catch {
          setOrder(null);
          setImageCount(0);
        }
      } else {
        setOrder(null);
        setImageCount(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch image count for the order
  const fetchImageCount = async (orderId) => {
    if (!orderId) return;
    try {
      const response = await axios.get(`${API}/orders/${orderId}/images`);
      setImageCount(response.data.images?.length || 0);
    } catch (error) {
      console.error("Error fetching image count:", error);
      setImageCount(0);
    }
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setEditedFields(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Get current value (edited or original)
  const getValue = (field) => {
    if (editedFields.hasOwnProperty(field)) {
      return editedFields[field];
    }
    return order?.[field] || "";
  };

  // Auto-save with debounce
  const saveChanges = useCallback(async () => {
    if (!order?.id || Object.keys(editedFields).length === 0) return;

    setSaving(true);
    try {
      await axios.put(`${API}/orders/${order.id}`, editedFields);
      
      // Update local state
      setOrder(prev => ({ ...prev, ...editedFields }));
      setEditedFields({});
      setHasChanges(false);
      
      // Show saved indicator for 2 seconds
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      
      // Auto-save completed - no need to refresh parent
      // Removed: onOrderUpdated() was causing blink by refreshing conversations
    } catch (error) {
      console.error("Error saving order:", error);
      const msg = error.response?.data?.detail || "Failed to save changes";
      toast.error(msg, { autoClose: 8000 });
    } finally {
      setSaving(false);
    }
  }, [order?.id, editedFields, onOrderUpdated]);

  // Auto-save when field changes (debounced)
  useEffect(() => {
    if (!hasChanges) return;
    
    const timer = setTimeout(() => {
      saveChanges();
    }, 1500); // 1.5 second debounce

    return () => clearTimeout(timer);
  }, [editedFields, hasChanges, saveChanges]);

  if (!conversation) {
    return (
      <aside
        className="w-72 border-l bg-muted/30 hidden lg:flex flex-col items-center justify-center p-8"
        data-testid="order-sidebar-empty"
      >
        <div className="text-center">
          <Package className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">Select a conversation</p>
          <p className="text-sm text-muted-foreground/70">
            Order details will appear here
          </p>
        </div>
      </aside>
    );
  }

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM d, yyyy 'at' hh:mm aa");
    } catch {
      return "N/A";
    }
  };

  return (
    <aside
      className="w-72 border-l bg-background hidden lg:flex flex-col shrink-0 overflow-hidden"
      data-testid="order-sidebar"
    >
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Order Details
          </h3>
          <div className="flex items-center">
            {saving && (
              <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving...
              </div>
            )}
            {!saving && saved && (
              <div className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                <CheckCircle className="h-3 w-3" />
                Saved
              </div>
            )}
            {!saving && !saved && hasChanges && (
              <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                <Save className="h-3 w-3" />
                Unsaved
              </div>
            )}
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : order ? (
            <>
              {/* Editable Order Fields */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Order Information
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 gap-1"
                    onClick={() => setImageGalleryOpen(true)}
                    data-testid="open-image-gallery-btn"
                  >
                    <Images className="h-3.5 w-3.5" />
                    <span className="text-xs">Images</span>
                    {imageCount > 0 && (
                      <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-0.5 bg-primary/10">
                        {imageCount}
                      </Badge>
                    )}
                  </Button>
                </div>

                {/* Order ID - Editable */}
                <div>
                  <Label className="text-xs text-muted-foreground">Order ID</Label>
                  <Input
                    placeholder="Enter Order ID"
                    value={getValue("order_id")}
                    onChange={(e) => handleFieldChange("order_id", e.target.value)}
                    className="mt-1 font-mono text-sm h-9"
                  />
                </div>

                {/* Order Status - Editable */}
                <div>
                  <Label className="text-xs text-muted-foreground">Order Status</Label>
                  <Select
                    value={getValue("order_id_status") || "NOT_SET"}
                    onValueChange={(value) => handleFieldChange("order_id_status", value === "NOT_SET" ? "" : value)}
                  >
                    <SelectTrigger className="mt-1 h-9">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {(dropdownOptions.order_id_status || []).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${opt.color?.split(' ')[0] || 'bg-gray-300'}`}></span>
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Account - Editable */}
                <div>
                  <Label className="text-xs text-muted-foreground">Account</Label>
                  <Select
                    value={getValue("account") || "NONE"}
                    onValueChange={(value) => handleFieldChange("account", value === "NONE" ? "" : value)}
                  >
                    <SelectTrigger className="mt-1 h-9">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {(dropdownOptions.account || []).map((opt) => (
                        <SelectItem key={opt.value || "NONE"} value={opt.value || "NONE"}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Product Details - Dropdown */}
                <div>
                  <Label className="text-xs text-muted-foreground">Product Details</Label>
                  <Select
                    value={getValue("product_details") || "NONE"}
                    onValueChange={(value) => handleFieldChange("product_details", value === "NONE" ? "" : value)}
                  >
                    <SelectTrigger className="mt-1 h-9">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {(dropdownOptions.product_details || []).map((opt) => (
                        <SelectItem key={opt.value || "NONE"} value={opt.value || "NONE"}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Design Status - Editable */}
                <div>
                  <Label className="text-xs text-muted-foreground">Design Status</Label>
                  <Select
                    value={getValue("design_status") || "PENDING"}
                    onValueChange={(value) => handleFieldChange("design_status", value)}
                  >
                    <SelectTrigger className="mt-1 h-9">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {(dropdownOptions.design_status || []).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${opt.color?.split(' ')[0] || 'bg-gray-300'}`}></span>
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Remarks - Editable */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">Remarks</Label>
                    {order?.remarks_updated_at && (
                      <span className="text-[9px] text-gray-400">
                        {new Date(order?.remarks_updated_at).toLocaleDateString('en-IN', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}
                      </span>
                    )}
                  </div>
                  <Input
                    placeholder="Add remarks..."
                    value={getValue("remarks")}
                    onChange={(e) => handleFieldChange("remarks", e.target.value)}
                    className="mt-1 text-sm h-9"
                  />
                </div>
              </div>

              <Separator />

              {/* Read-only Info */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Details
                </h4>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Session</span>
                  <Badge variant="outline" className="font-mono text-xs">
                    {order.session_id}
                  </Badge>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Images</span>
                  <div className="flex items-center gap-1">
                    <Image className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{order.total_image_count}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Phone</span>
                  <span className="font-mono text-xs">{order.phone_number}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Customer</span>
                  <span>{order.customer_name || "Unknown"}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span className="text-xs">{formatDate(order.first_message_date)}</span>
                </div>

                {order.rto_flag === "YES" && (
                  <Badge variant="destructive" className="w-full justify-center">
                    RTO FLAG
                  </Badge>
                )}
              </div>

              <Separator />

              {/* Quick Actions */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Actions
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between"
                  onClick={() => navigate(`/orders?search=${encodeURIComponent(order.phone_number)}`)}
                >
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    View in Database
                  </div>
                  <ChevronRight className="h-4 w-4" />
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between"
                  onClick={() => navigate(`/images?search=${encodeURIComponent(order.phone_number)}`)}
                >
                  <div className="flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    View Images ({order.total_image_count || imageCount})
                  </div>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Previous Orders Section */}
              {previousOrders.length > 1 && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <History className="h-3 w-3" />
                      Previous Orders ({previousOrders.length - 1})
                    </h4>
                    {loadingHistory ? (
                      <div className="flex items-center justify-center py-2">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {previousOrders
                          .filter((o) => o.id !== order?.id)
                          .slice(0, 5)
                          .map((prevOrder) => (
                            <div
                              key={prevOrder.id}
                              className="p-2 bg-muted/50 rounded-lg text-xs space-y-1"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-mono font-medium">
                                  {prevOrder.order_id || "No ID"}
                                </span>
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-1.5 py-0 ${
                                    (dropdownOptions.design_status || []).find(
                                      (s) => s.value === prevOrder.design_status
                                    )?.color || ""
                                  }`}
                                >
                                  {prevOrder.design_status}
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatDate(prevOrder.created_at)}
                                </span>
                                <span>{prevOrder.session_id}</span>
                              </div>
                              {prevOrder.design_generated_time && (
                                <div className="text-muted-foreground flex items-center gap-1">
                                  <span>✓</span>
                                  <span>{prevOrder.design_status === "DESIGNED" ? "Designed" : "Ready"}: </span>
                                  <span className="font-medium text-foreground">{formatDate(prevOrder.design_generated_time)}</span>
                                </div>
                              )}
                              {prevOrder.account && (
                                <div className="text-muted-foreground">
                                  Account: <span className="font-medium text-foreground">{prevOrder.account}</span>
                                </div>
                              )}
                              {prevOrder.product_details && (
                                <div className="text-muted-foreground">
                                  Product: {prevOrder.product_details}
                                </div>
                              )}
                              {prevOrder.remarks && (
                                <div className="text-muted-foreground">
                                  Remarks: <span className="font-medium text-foreground">{prevOrder.remarks}</span>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {/* No order found */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Conversation Info
                </h4>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant="outline">{conversation.status}</Badge>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Phone</span>
                  <span className="font-mono text-xs">{conversation.phone_number}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">First Contact</span>
                  <span className="text-xs">{formatDate(conversation.created_at)}</span>
                </div>
              </div>

              <Separator />

              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Package className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  No order linked yet
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Order will be created when customer sends details
                </p>
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      {/* Image Gallery Popup */}
      <ImageGalleryPopup
        orderId={order?.id}
        orderInfo={{ ...order, image_folder: conversation?.image_folder }}
        isOpen={imageGalleryOpen}
        onClose={() => setImageGalleryOpen(false)}
        onImagesUpdated={() => {
          fetchOrder();
          if (order?.id) fetchImageCount(order.id);
        }}
      />
    </aside>
  );
}
